import * as React from "react";

import {
  faCircleCheck,
  faCircleInfo,
  faEnvelope,
  faHeadset,
  faPhone,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatAccountRepDisplayName,
  formatAccountRepPhotoFilename,
  formatPhoneNumber,
} from "./util/stringUtil";
import {
  selectDepartmentId,
  selectDepartmentType,
  selectHospitalId,
  selectUserInfo,
} from "../../../src/redux/app";
import { useLazyQuery, useMutation } from "@apollo/client";

import { ACCOUNT_REP_PHOTOS_URL } from "../constants";
import { Button } from "./base/Button";
import { DepartmentType } from "./__generated__/graphql";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { P2 } from "@verse_core/shared_components/base/TextComponents/P2";
import { P2B } from "@verse_core/shared_components/base/TextComponents/P2B";
import { RadioInput } from "./base/RadioInput";
import { gql } from "./__generated__";
import { useAppSelector } from "../../../src/redux/hooks";
import useKustomerWidget from "./useKustomerWidget";
import { useLocation } from "react-router-dom";

export const GET_SALES_REP = gql(`
    query GET_SALES_REP(
        $hospitalId: Int!
    ) {
        getAccountRep(hospitalId: $hospitalId) {
            id
            name
            slackId
            phone
            email
            customerSuccessTeam
            calendarPageUrl
        }
    }
`);

const UPDATE_PROVIDER_USER_PHONE = gql(`
    mutation UPDATE_PROVIDER_USER_PHONE(
        $spreeUserId: Int!
        $phone: String!
    ) {
        updateProviderUserPhone(
            spreeUserId: $spreeUserId
            phone: $phone
        )
    }
`);

export const SEND_SALES_REP_MESSAGE = gql(`
    mutation SEND_SALES_REP_MESSAGE(
        $salesRepInput: SalesRepMessageInput!
    ){
        sendSalesRepMessage(
            salesRepInput: $salesRepInput
        )
    }
`);

const EditCustomerPhone = (props: { phone: string; userId: number }) => {
  const [edit, setEdit] = React.useState(false);
  const [phone, setPhone] = React.useState("");
  const [error, setError] = React.useState("");

  const [updateProviderUserPhone] = useMutation(UPDATE_PROVIDER_USER_PHONE, {
    fetchPolicy: "no-cache",
  });

  React.useEffect(() => {
    // when prop is passed down, can be null/undefined
    if (!props.phone) {
      setEdit(true);
    }
    setPhone(props.phone);
  }, [props.phone]);

  return edit ? (
    <span className="tw-ml-[5px] tw-mt-[-6px] tw-font-normal">
      <input
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        type="text"
        placeholder="Phone number"
        className="tw-rounded-md tw-border-[2px] tw-border-solid tw-border-blue-2 tw-p-[6px] tw-text-[12px] tw-shadow-sm tw-outline-none placeholder:tw-text-gray-5 focus:tw-border-[2px] focus:tw-shadow-sm focus-visible:tw-border-[2px] focus-visible:tw-shadow-sm"
      ></input>
      <Button
        onClick={() => {
          updateProviderUserPhone({
            variables: {
              spreeUserId: props.userId,
              phone: phone,
            },
            onCompleted: () => {
              setEdit(false);
              setError("");
            },
            onError: () => {
              setError("Error");
            },
          });
        }}
        className="tw-ml-[5px] tw-p-[6px] tw-text-[12px]"
      >
        Done
      </Button>
      {error ? (
        <span className="tw-ml-[5px] tw-text-red-1">{error}</span>
      ) : null}
    </span>
  ) : (
    <span className="tw-ml-[5px] tw-font-normal tw-text-gray-3">
      {phone}
      <span
        className="tw-ml-[5px] tw-cursor-pointer tw-text-blue-1 tw-underline"
        onClick={() => {
          setEdit(true);
        }}
      >
        {phone.length > 0 ? "Edit" : "Add"}
      </span>
    </span>
  );
};

const CustomerServicePopup = () => {
  const [expanded, setExpanded] = React.useState(false);

  const [getSalesRep, { data: salesRepData, called, loading }] = useLazyQuery(
    GET_SALES_REP,
    {
      fetchPolicy: "no-cache", // need no-cache to stop errors from persisting
    },
  );

  const [contactMethod, setContactMethod] = React.useState("phone");
  const [message, setMessage] = React.useState("");
  const [showSuccess, setShowSuccess] = React.useState(false);
  const salesRep = salesRepData?.getAccountRep ?? null;
  const isCustomerSuccessTeam = salesRep?.customerSuccessTeam ?? false;
  const currentUserInfo = useAppSelector(selectUserInfo);
  const providerEmail = currentUserInfo.email;
  const userId = currentUserInfo.id;
  const userPhone = currentUserInfo.userSettings.phone ?? "";
  const [sendSalesRepMessage] = useMutation(SEND_SALES_REP_MESSAGE, {
    fetchPolicy: "no-cache",
  });
  const hospitalId = useAppSelector(selectHospitalId);
  const departmentId = useAppSelector(selectDepartmentId);
  const departmentType = useAppSelector(selectDepartmentType);
  const useOldWidget =
    departmentType === DepartmentType.CaseManagement ||
    departmentType === DepartmentType.Enterprise;

  const location = useLocation();
  const orderNumberMatch = location.pathname.match(
    /\/provider_orders\/(?:view_order|edit|draft(?:\/sign)?|order_verification)\/([^/]+)/,
  );
  const orderNumber = orderNumberMatch ? orderNumberMatch[1] : null;

  React.useEffect(() => {
    getSalesRep({
      variables: {
        hospitalId: hospitalId,
      },
    });
  }, [hospitalId]);

  const salesRepLoaded = called && !loading;
  const enableKustomer =
    salesRepLoaded && (isCustomerSuccessTeam || !salesRep) && !useOldWidget;
  useKustomerWidget(enableKustomer, {
    email: providerEmail,
    phone: userPhone,
    departmentId,
    orderNumber,
  });

  if (enableKustomer) return null;

  return salesRep ? (
    <div className="tw-fixed tw-bottom-[13px] tw-right-[13px] tw-z-[1000]">
      {expanded ? (
        <div className="tw-w-[480px] tw-rounded-[18px] tw-border-[2px] tw-border-solid tw-border-blue-2 tw-bg-white tw-p-[24px] tw-shadow-md">
          <div
            onClick={() => setExpanded(false)}
            className="tw-border-1 tw-absolute tw-right-[-5px] tw-top-[-10px] tw-cursor-pointer tw-rounded-full tw-border-solid tw-border-blue-2 tw-bg-white tw-p-[5px] tw-px-[10px] tw-text-center tw-align-middle tw-text-[15px] tw-text-blue-1"
          >
            <FontAwesomeIcon icon={faXmark} />
          </div>
          <div className="tw-flex tw-flex-row">
            <div className="tw-mr-[24px]">
              <img
                className="tw-h-[80px] tw-w-[80px] tw-rounded-full tw-object-cover"
                src={
                  salesRep.name === "Holding"
                    ? `${ACCOUNT_REP_PHOTOS_URL}/adamyecies.jpg`
                    : `${ACCOUNT_REP_PHOTOS_URL}/${formatAccountRepPhotoFilename(salesRep.name)}`
                }
              />
            </div>
            <div>
              <span className="tw-mr-[5px] tw-font-sans tw-text-[20px] tw-font-bold tw-tracking-[-.03px] tw-text-blue-dark-1">
                {formatAccountRepDisplayName(salesRep.name)}
              </span>
              <span className="tw-font-sans tw-text-[14px] tw-text-blue-1">
                - Your account representative
              </span>
              <P2 className="tw-mt-[5px] tw-text-gray-3">
                Feel free to reach out to me if you have any questions regarding
                your experience with us.
                <P2 className="tw-mt-[12px]">
                  <span className="tw-block tw-text-gray-0">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="tw-mr-[5px] tw-text-gray-4"
                    />
                    {formatPhoneNumber(salesRep.phone ?? "")}
                  </span>
                </P2>
                <P2>
                  <span className="tw-block tw-text-gray-0">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="tw-mr-[8px] tw-text-gray-4"
                    />
                    {salesRep.email}
                  </span>
                </P2>
              </P2>
            </div>
          </div>
          <P2B className="tw-mb-[8px] tw-mt-[10px] tw-text-gray-0">
            Experiencing any issues? Feel free to send me a message. I will get
            back to you ASAP:
          </P2B>
          <div className="tw-flex tw-flex-row tw-rounded-md tw-border-[1px] tw-border-solid tw-border-blue-2 tw-bg-blue-light-1 tw-p-[5px]">
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="tw-mx-[3px] tw-mt-[12.5px] tw-text-blue-1"
            />{" "}
            <span className="tw-ml-[5px] tw-block">
              For order-specific issues, please use the order note feature on
              each order page.
            </span>
          </div>
          <textarea
            className="tw-mt-[6px] tw-h-[160px] tw-w-full tw-resize-none tw-overflow-y-scroll tw-rounded-[4px] tw-border-[1px] tw-border-solid tw-border-gray-5 tw-p-[15px]"
            placeholder="Type your message here..."
            onChange={(e) => setMessage(e.target.value)}
          ></textarea>
          <P2B className="tw-mb-[8px] tw-mt-[10px] tw-text-gray-0">
            What is the best way to get back to you?
          </P2B>
          <div className="tw-rounded-md tw-bg-gray-light-3 tw-p-0 tw-px-[3px]">
            <RadioInput
              title="customer-service-response-options"
              options={[
                {
                  name: "Via phone",
                  value: "phone",
                  children: (
                    <EditCustomerPhone phone={userPhone} userId={userId} />
                  ),
                  dataTestId: "customer-service-response-options_via-phone",
                },
                {
                  name: "Via email",
                  value: "email",
                  children: (
                    <span className="tw-ml-[5px] tw-font-normal tw-text-gray-3">
                      {providerEmail}
                    </span>
                  ),
                  dataTestId: "customer-service-response-options_via-email",
                },
              ]}
              changeValue={contactMethod}
              changeFunction={(value) => {
                setContactMethod(value);
              }}
              alignment="vertical"
            />
          </div>
          <div className="tw-mt-[16px] tw-flex tw-flex-row">
            <div className="tw-w-1/2 tw-p-0 tw-pr-[8px]">
              <Button
                onClick={() => setExpanded(false)}
                className="tw-font-500 tw-w-full tw-border-gray-light-2 tw-bg-gray-light-2 tw-text-[14px] tw-text-gray-0 hover:tw-border-gray-light-0 hover:tw-bg-gray-light-0 hover:tw-text-gray-0 active:tw-shadow-none"
              >
                Cancel
              </Button>
            </div>
            <div className="tw-w-1/2 tw-p-0 tw-pl-[8px]">
              <Button
                onClick={() => {
                  if (message.length > 0) {
                    sendSalesRepMessage({
                      variables: {
                        salesRepInput: {
                          salesRepId: salesRep.id,
                          message: message,
                          method: contactMethod,
                          userId: userId,
                          hospitalId: hospitalId,
                          orderNumber: orderNumber,
                        },
                      },
                      onCompleted: () => {
                        setShowSuccess(true);
                        setExpanded(false);
                        setTimeout(() => {
                          setShowSuccess(false);
                        }, 5000);
                      },
                    });
                  }
                }}
                className={`tw-font-500 tw-w-full tw-text-[14px] hover:tw-text-white hover:tw-outline-none active:tw-text-white ${
                  message.length === 0
                    ? "tw-opacity-50 hover:tw-border-blue-2 hover:tw-bg-blue-2"
                    : "hover:tw-border-blue-0"
                }`}
                tooltip={
                  message.length === 0 ? "The message is empty." : undefined
                }
              >
                Send message
              </Button>
            </div>
          </div>
        </div>
      ) : showSuccess ? (
        <div className="tw-mb-[10px] tw-mr-[13px] tw-flex tw-animate-popUpDisappearSlideLeft tw-flex-row tw-rounded-md tw-border-[1px] tw-border-solid tw-border-green-1 tw-bg-green-light-1 tw-shadow-sm">
          <div className="tw-w-[4px] tw-bg-green-1" />
          <div className="tw-p-[8px] tw-pr-[13px]">
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="tw-mr-[3px] tw-text-green-1"
            />{" "}
            Message sent. We will get back to you shortly.
          </div>
        </div>
      ) : (
        <Button
          onClick={() => setExpanded(true)}
          className="tw-mb-[10px] tw-mr-[13px] tw-rounded-full tw-text-[14px] tw-shadow-sm2"
        >
          <FontAwesomeIcon icon={faHeadset} /> Customer Support
        </Button>
      )}
    </div>
  ) : null;
};

export default CustomerServicePopup;
